import { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ChannelPicker, CHANNEL_TYPE } from '@/components/ChannelPicker';
import { RolePicker } from '@/components/RolePicker';
import { Trash2, Upload } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { useConfigPage, BackLink } from './_shared';

/**
 * AutoMod-Config — consumer is AutomodMessageCreate.js, which expects the
 * nested config shape:
 *   autoMod = {
 *     enabled, channelId,
 *     inviteLinks: { enabled, excludedChannels },
 *     normalLinks: { enabled, excludedChannels },
 *     badWords:    { enabled, excludedChannels },
 *     antiSpam:    { enabled, excludedChannels },
 *     badWordsList, excludedRoles,
 *     onlyImageChannelIds, onlyYouTubeLinksChannelIds,
 *   }
 * We render one big form for all of it — per-feature excluded-channels lists
 * are stashed into their sub-config on save.
 */
type SubFeatureKey = 'inviteLinks' | 'normalLinks' | 'badWords' | 'antiSpam';
const SUB_FEATURES: { key: SubFeatureKey; label: string; desc: string }[] = [
  { key: 'inviteLinks', label: 'Discord Invite Links', desc: 'Löscht Nachrichten mit `discord.gg/…`' },
  { key: 'normalLinks', label: 'Externe Links',         desc: 'Löscht Nachrichten mit `http(s)://…` (YouTube-Only-Channels ausgenommen)' },
  { key: 'badWords',    label: 'Bad Words',             desc: 'Löscht Nachrichten die genau einem Wort aus der Liste unten entsprechen' },
  { key: 'antiSpam',    label: 'Anti-Spam',             desc: 'Löscht wenn ein User >4 Nachrichten in 5 Sekunden schickt' },
];

interface SubConfig {
  enabled: boolean;
  excludedChannels: string[];
}
const emptySub = (): SubConfig => ({ enabled: false, excludedChannels: [] });

export function AutoModConfig() {
  const { guildId, settingsQuery, saveMut, currentConfig } = useConfigPage('autoMod');

  const [enabled, setEnabled] = useState(false);
  const [channelId, setChannelId] = useState<string | null>(null);
  const [subs, setSubs] = useState<Record<SubFeatureKey, SubConfig>>({
    inviteLinks: emptySub(),
    normalLinks: emptySub(),
    badWords: emptySub(),
    antiSpam: emptySub(),
  });
  const [badWordsList, setBadWordsList] = useState('');
  const [excludedRoleId, setExcludedRoleId] = useState<string | null>(null);
  const [imageOnly, setImageOnly] = useState<string[]>([]);
  const [youtubeOnly, setYoutubeOnly] = useState<string[]>([]);
  const [imageOnlyPicker, setImageOnlyPicker] = useState<string | null>(null);
  const [youtubeOnlyPicker, setYoutubeOnlyPicker] = useState<string | null>(null);

  useEffect(() => {
    if (!settingsQuery.data) return;
    setEnabled((currentConfig.enabled as boolean) ?? false);
    setChannelId((currentConfig.channelId as string) ?? null);
    setSubs({
      inviteLinks: (currentConfig.inviteLinks as SubConfig) ?? emptySub(),
      normalLinks: (currentConfig.normalLinks as SubConfig) ?? emptySub(),
      badWords: (currentConfig.badWords as SubConfig) ?? emptySub(),
      antiSpam: (currentConfig.antiSpam as SubConfig) ?? emptySub(),
    });
    setBadWordsList(
      Array.isArray(currentConfig.badWordsList)
        ? (currentConfig.badWordsList as string[]).join('\n')
        : ''
    );
    setExcludedRoleId((currentConfig.excludedRoles as string) ?? null);
    setImageOnly(
      Array.isArray(currentConfig.onlyImageChannelIds)
        ? (currentConfig.onlyImageChannelIds as string[])
        : []
    );
    setYoutubeOnly(
      Array.isArray(currentConfig.onlyYouTubeLinksChannelIds)
        ? (currentConfig.onlyYouTubeLinksChannelIds as string[])
        : []
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQuery.data]);

  if (settingsQuery.isLoading) return <p className="text-muted-foreground">Lade…</p>;

  const toggleSub = (key: SubFeatureKey, v: boolean) =>
    setSubs((prev) => ({ ...prev, [key]: { ...prev[key], enabled: v } }));

  const addToList = (
    list: string[],
    setter: (v: string[]) => void,
    val: string | null,
    reset: (v: string | null) => void
  ) => {
    if (val && !list.includes(val)) setter([...list, val]);
    reset(null);
  };

  return (
    <div className="mx-auto max-w-2xl">
      <BackLink guildId={guildId} />
      <h1 className="mb-6 text-2xl font-semibold">AutoMod — Konfiguration</h1>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-lg">Grundeinstellungen</CardTitle>
          <CardDescription>Aktiviert die AutoMod-Verarbeitung und setzt den Log-Kanal.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Switch
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            label="AutoMod aktiviert"
          />
          <div>
            <Label>Log-Channel (wo Löschungen protokolliert werden)</Label>
            <ChannelPicker guildId={guildId} value={channelId} onChange={setChannelId} types={[CHANNEL_TYPE.GUILD_TEXT]} />
          </div>
          <div>
            <Label>Ausgeschlossene Rolle (User mit dieser Rolle werden ignoriert)</Label>
            <RolePicker guildId={guildId} value={excludedRoleId} onChange={setExcludedRoleId} />
            <p className="mt-1 text-xs text-muted-foreground">Discord-Admins sind zusätzlich immer ausgenommen.</p>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-lg">Filter-Module</CardTitle>
          <CardDescription>Einzelne Filter an/ausschalten.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {SUB_FEATURES.map((f) => (
            <div key={f.key} className="flex items-start gap-3">
              <Switch
                checked={subs[f.key].enabled}
                onChange={(e) => toggleSub(f.key, e.target.checked)}
                className="mt-0.5"
              />
              <div className="flex-1">
                <div className="text-sm font-medium">{f.label}</div>
                <div className="text-xs text-muted-foreground">{f.desc}</div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-lg">Bad Words</CardTitle>
          <CardDescription>Ein Wort pro Zeile. Bad-Words-Filter muss oben aktiv sein.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <BadWordsImport onImport={setBadWordsList} />
          <Textarea
            rows={8}
            value={badWordsList}
            onChange={(e) => setBadWordsList(e.target.value)}
            placeholder="wort1&#10;wort2&#10;..."
            className="font-mono text-xs"
          />
          <p className="mt-1 text-right text-xs text-muted-foreground">
            {badWordsList.split(/\r?\n/).filter((w) => w.trim()).length} Wörter
          </p>
        </CardContent>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-lg">Image-Only Channels</CardTitle>
          <CardDescription>In diesen Channels werden Nachrichten ohne Bild-Attachment gelöscht.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <ChannelList
            channelIds={imageOnly}
            onRemove={(id) => setImageOnly(imageOnly.filter((c) => c !== id))}
          />
          <div className="flex gap-2">
            <ChannelPicker
              guildId={guildId}
              value={imageOnlyPicker}
              onChange={setImageOnlyPicker}
              types={[CHANNEL_TYPE.GUILD_TEXT]}
              placeholder="— Channel hinzufügen —"
            />
            <Button
              onClick={() => addToList(imageOnly, setImageOnly, imageOnlyPicker, setImageOnlyPicker)}
              disabled={!imageOnlyPicker}
            >
              +
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-lg">YouTube-Only Channels</CardTitle>
          <CardDescription>Nur YouTube-Links erlaubt — alles andere wird gelöscht (nur wenn „Externe Links" oben aktiv).</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <ChannelList
            channelIds={youtubeOnly}
            onRemove={(id) => setYoutubeOnly(youtubeOnly.filter((c) => c !== id))}
          />
          <div className="flex gap-2">
            <ChannelPicker
              guildId={guildId}
              value={youtubeOnlyPicker}
              onChange={setYoutubeOnlyPicker}
              types={[CHANNEL_TYPE.GUILD_TEXT]}
              placeholder="— Channel hinzufügen —"
            />
            <Button
              onClick={() =>
                addToList(youtubeOnly, setYoutubeOnly, youtubeOnlyPicker, setYoutubeOnlyPicker)
              }
              disabled={!youtubeOnlyPicker}
            >
              +
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-3">
        {saveMut.isSuccess && <span className="text-sm text-success">Gespeichert ✓</span>}
        {saveMut.error && (
          <span className="text-sm text-destructive">
            Fehler: {(saveMut.error as Error).message}
          </span>
        )}
        <Button
          disabled={saveMut.isPending}
          onClick={() =>
            saveMut.mutate({
              moduleConfig: {
                enabled,
                channelId,
                inviteLinks: subs.inviteLinks,
                normalLinks: subs.normalLinks,
                badWords: subs.badWords,
                antiSpam: subs.antiSpam,
                badWordsList: badWordsList
                  .split(/\r?\n/)
                  .map((w) => w.trim().toLowerCase())
                  .filter(Boolean),
                excludedRoles: excludedRoleId,
                onlyImageChannelIds: imageOnly,
                onlyYouTubeLinksChannelIds: youtubeOnly,
              },
            })
          }
        >
          {saveMut.isPending ? 'Speichere…' : 'Alles speichern'}
        </Button>
      </div>
    </div>
  );
}

function ChannelList({
  channelIds,
  onRemove,
}: {
  channelIds: string[];
  onRemove: (id: string) => void;
}) {
  if (channelIds.length === 0) {
    return <p className="text-sm text-muted-foreground">Keine Channels konfiguriert.</p>;
  }
  return (
    <ul className="space-y-1">
      {channelIds.map((id) => (
        <li
          key={id}
          className="flex items-center justify-between rounded-md border p-2 text-sm"
        >
          <code className="truncate">{id}</code>
          <Button variant="ghost" size="icon" onClick={() => onRemove(id)} title="Entfernen">
            <Trash2 className="h-4 w-4" />
          </Button>
        </li>
      ))}
    </ul>
  );
}

/**
 * TXT-Datei mit Wörtern hochladen (ein Wort pro Zeile). Ersetzt die
 * aktuelle Liste in der Textarea; Save-Button oben commit dann in die DB.
 * Matches the /setbadwords slash command's `\r?\n`-split behaviour.
 */
function BadWordsImport({ onImport }: { onImport: (v: string) => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="flex items-center gap-2">
      <input
        ref={fileRef}
        type="file"
        accept=".txt,text/plain"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = ''; // reset so re-selecting same file re-triggers
          if (!file) return;
          if (!file.name.toLowerCase().endsWith('.txt')) {
            setError('Nur .txt-Dateien.');
            return;
          }
          try {
            const text = await file.text();
            const words = text
              .split(/\r?\n/)
              .map((w) => w.trim())
              .filter(Boolean);
            onImport(words.join('\n'));
            setError(null);
          } catch (err) {
            setError((err as Error).message);
          }
        }}
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => fileRef.current?.click()}
      >
        <Upload className="mr-1 h-3.5 w-3.5" /> TXT importieren
      </Button>
      <span className="text-xs text-muted-foreground">
        Ersetzt die Liste. Anschließend Speichern drücken.
      </span>
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}
